import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { AppearanceChoice as AppearanceChoiceSchema, type AppearanceChoice } from "@townsy/contracts";
import { errorMessage, isApiError } from "../../api/client";
import { putAppearance } from "../../api/endpoints";
import { useBootstrapQuery, useInvalidateIdentity, useMeQuery, useMyHomeQuery, useWalletsQuery } from "../../api/queries";
import { displayNameProblem, requestPasswordReset, resendVerificationEmail, signInWithEmail, signOut, signUpWithEmail } from "../../auth/authClient";
import { stageLabel } from "../../homes/homeLogic";
import { flagLabel } from "../../lib/flag";
import { STORAGE_KEYS, readJson, remove, writeJson } from "../../lib/storage";
import { formatCoins, formatCredit } from "../../lib/wallet";
import { AvatarBuilder } from "../../ui/avatar/AvatarBuilder";
import { AvatarPreview } from "../../ui/avatar/AvatarPreview";
import { choiceFromAppearance, coherentStarter } from "../../ui/avatar/avatarModel";
import { PageShell } from "../../ui/PageShell";
import type { SignUpDoneState } from "./SignUpDonePage";
import { MyOrdersList } from "../../ui/checkout/MyOrdersList";
import { FlagIcon } from "../../ui/FlagIcon";
import { DmSettingsCard } from "../../ui/messages/DmSettingsCard";
import { WorldVisibilityCard } from "../../ui/worlds/WorldVisibilityCard";
import { DeletionNotice, PrivacySection } from "../../ui/privacy/PrivacySection";
import { SignInCodeForm, TwoStepSection } from "../../ui/account/TwoStepSection";

/**
 * `/account`: sign up (display name + avatar), sign in, sign out through the Better Auth React
 * client, and "Edit avatar" for residents. Identity is derived server-side; after any change the
 * bootstrap is refetched and the town re-joins with the new actor. Guests keep their random look.
 */

/** Save an avatar choice for the signed-in resident; keeps it for after sign-in when there is no session yet. */
async function saveAppearance(choice: AppearanceChoice): Promise<{ ok: true } | { ok: false; message: string; needsSignIn: boolean }> {
  try {
    await putAppearance(choice);
    remove("local", STORAGE_KEYS.pendingAppearance);
    return { ok: true };
  } catch (err) {
    // 401 = no session; 403 = still the guest actor (an account that must verify its email first is not signed in
    // yet, so the request is the guest's: "Guests keep their assigned look"). Either way: keep the choice for later.
    const needsSignIn = isApiError(err) && (err.status === 401 || err.status === 403);
    // Local (not session) storage: the verification link usually opens in a new tab.
    if (needsSignIn) writeJson("local", STORAGE_KEYS.pendingAppearance, choice);
    return { ok: false, message: errorMessage(err, "Your avatar could not be saved."), needsSignIn };
  }
}

function pendingAppearance(): AppearanceChoice | null {
  const parsed = AppearanceChoiceSchema.safeParse(readJson<unknown>("local", STORAGE_KEYS.pendingAppearance));
  return parsed.success ? parsed.data : null;
}

export function AccountPage() {
  const bootstrapQ = useBootstrapQuery();
  const bootstrap = bootstrapQ.data;
  const signedIn = Boolean(bootstrap?.session.userId);
  const me = useMeQuery(signedIn);
  const wallets = useWalletsQuery(signedIn);
  const invalidate = useInvalidateIdentity();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // "Sign up (18+) to play" in the games links to /account?tab=signup (ADR-0048).
  const [tab, setTab] = useState<"signin" | "signup">(params.get("tab") === "signup" ? "signup" : "signin");
  const [forgot, setForgot] = useState(params.get("forgot") === "1");
  /** ADR-0039: the password was right and the account asks for its two-step sign-in code. */
  const [codeStep, setCodeStep] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const registrationEnabled = bootstrap?.features.registrationEnabled ?? true;
  // ADR-0047: adults only (18+): sign-up needs the confirmation (the server refuses it otherwise).
  const adultsOnly = bootstrap?.features.adultsOnly === true;
  const [adultConfirmed, setAdultConfirmed] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const nameProblem = displayName ? displayNameProblem(displayName) : null;

  // The sign-up builder starts from the guest's current (random) look; the resident editor from theirs.
  const actorAppearance = bootstrap?.actor.appearance ?? null;
  const [avatar, setAvatar] = useState<AppearanceChoice | null>(null);
  const isGuest = bootstrap?.actor.kind === "guest";
  const avatarValue = avatar ?? (isGuest ? coherentStarter(choiceFromAppearance(actorAppearance)) : choiceFromAppearance(actorAppearance));
  const [editing, setEditing] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarMessage, setAvatarMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);

  // A choice made at sign-up that could not be saved (no session yet) is applied once signed in.
  const invalidateRef = useRef(invalidate);
  invalidateRef.current = invalidate;
  useEffect(() => {
    if (!signedIn) return;
    const pending = pendingAppearance();
    if (!pending) return;
    let cancelled = false;
    void saveAppearance(pending).then(async (res) => {
      if (cancelled || !res.ok) return;
      setAvatarMessage({ tone: "success", text: "Saved the avatar you chose when you signed up." });
      await invalidateRef.current();
    });
    return () => {
      cancelled = true;
    };
  }, [signedIn]);

  const onSignIn = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMessage(null);
    const res = await signInWithEmail({ email: email.trim(), password });
    setBusy(false);
    if (!res.ok) {
      setMessage({ tone: "error", text: res.message ?? "Sign-in failed." });
      return;
    }
    setPassword("");
    if (res.twoFactor) {
      setCodeStep(true);
      return;
    }
    setMessage({ tone: "success", text: "Signed in. Your town avatar now uses your account." });
    await invalidate();
  };

  const onCodeSignedIn = async (how: "totp" | "backup") => {
    setCodeStep(false);
    setMessage({
      tone: "success",
      text:
        how === "backup"
          ? "Signed in with a backup code. That code cannot be used again; check how many you have left under Two-step sign-in."
          : "Signed in. Your town avatar now uses your account.",
    });
    await invalidate();
  };

  const onSignUp = async (e: FormEvent) => {
    e.preventDefault();
    if (nameProblem || !displayName) {
      setMessage({ tone: "error", text: nameProblem ?? "Choose a display name." });
      return;
    }
    if (adultsOnly && !adultConfirmed) {
      setMessage({ tone: "error", text: "Townsy is for adults: confirm that you are 18 or older." });
      return;
    }
    setBusy(true);
    setMessage(null);
    const res = await signUpWithEmail({ displayName, email: email.trim(), password, ageConfirmed: adultsOnly && adultConfirmed });
    if (!res.ok) {
      setBusy(false);
      setMessage({ tone: "error", text: res.message ?? "Registration failed." });
      return;
    }
    // The new account is signed in: store the avatar chosen in the builder (server validates the ids).
    const saved = await saveAppearance(avatarValue);
    setBusy(false);
    setPassword("");
    const emailSent = bootstrap?.environment !== "local";
    const done: SignUpDoneState = {
      email: email.trim(),
      displayName,
      emailSent,
      outcome: saved.ok ? "signedIn" : saved.needsSignIn ? (emailSent ? "verifyThenSignIn" : "signInLater") : "avatarFailed",
      ...(saved.ok || saved.needsSignIn ? {} : { avatarError: saved.message }),
    };
    setAvatar(null);
    await invalidate();
    // Founder 2026-10-27: a confirmation page, not a message at the top of the long form.
    navigate("/account/created", { state: done });
  };

  const onSaveAvatar = async () => {
    setAvatarBusy(true);
    setAvatarMessage(null);
    const res = await saveAppearance(avatarValue);
    setAvatarBusy(false);
    if (!res.ok) {
      setAvatarMessage({ tone: "error", text: res.needsSignIn ? "Your session ended. Sign in again to save your avatar." : res.message });
      return;
    }
    setAvatarMessage({ tone: "success", text: "Avatar saved. Everyone in town sees the new look." });
    setEditing(false);
    setAvatar(null);
    await invalidate();
  };

  const onSignOut = async () => {
    setBusy(true);
    const res = await signOut();
    setBusy(false);
    if (!res.ok) {
      setMessage({ tone: "error", text: res.message ?? "Sign-out failed." });
      return;
    }
    setMessage({ tone: "success", text: "Signed out. You are a guest again." });
    setEditing(false);
    setAvatar(null);
    await invalidate();
  };

  return (
    <PageShell title="Account" narrow>
      <VerifiedBanner verified={params.get("verified") === "1"} error={params.get("error")} />
      <DeletionNotice signedIn={signedIn} />
      {bootstrapQ.isPending ? <p className="muted">Loading your session…</p> : null}
      {bootstrapQ.error ? (
        <div className="notice notice-danger" role="alert">
          Couldn't load your session: {errorMessage(bootstrapQ.error)}{" "}
          <button type="button" className="btn btn-sm" onClick={() => void bootstrapQ.refetch()}>
            Retry
          </button>
        </div>
      ) : null}

      {bootstrap ? (
        <div className="auth-card" style={{ marginBottom: 16 }}>
          <h2 style={{ fontSize: "1rem" }}>Who you are right now</h2>
          <div className="row" style={{ alignItems: "flex-start", flexWrap: "nowrap" }}>
            <AvatarPreview choice={choiceFromAppearance(actorAppearance)} size={96} />
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: 0 }}>
                <FlagIcon code={bootstrap.actor.countryCode} />
                <span className="visually-hidden">{flagLabel(bootstrap.actor.countryCode)} </span>{" "}
                <strong>{signedIn && bootstrap.session.displayName ? bootstrap.session.displayName : bootstrap.actor.label}</strong>{" "}
                <span className="muted">({signedIn ? "registered resident" : "guest"})</span>
              </p>
              <p className="hint" style={{ marginTop: 6 }}>
                The flag is an approximate country from the server, never your exact location. Guest numbers are server-generated.
                {signedIn ? "" : " Guests get a random look; create an account to choose your own."}
              </p>
            </div>
          </div>
          {signedIn ? (
            <>
              {me.data ? (
                <dl className="kv" style={{ marginTop: 8 }}>
                  <dt>Email</dt>
                  <dd>{typeof me.data.email === "string" ? me.data.email : "—"}</dd>
                  <dt>Verified</dt>
                  <dd>{me.data.emailVerified === undefined ? (bootstrap.session.emailVerified ? "yes" : "no") : me.data.emailVerified ? "yes" : "no"}</dd>
                  <dt>Roles</dt>
                  <dd>{(me.data.roles ?? bootstrap.session.roles).join(", ") || "resident"}</dd>
                  <dt>Coins</dt>
                  <dd>
                    <span className="wallet-chip wallet-chip-coin wallet-chip-static">{wallets.data ? formatCoins(wallets.data.coins.available) : "—"}</span>
                  </dd>
                  <dt>Townsy Credit</dt>
                  <dd>
                    <span className="wallet-chip wallet-chip-credit wallet-chip-static">
                      {wallets.data ? formatCredit(wallets.data.credit.availableCents) : "—"}
                    </span>
                  </dd>
                </dl>
              ) : me.error && !(isApiError(me.error) && me.error.status === 401) ? (
                <p className="small error-text">Profile details unavailable: {errorMessage(me.error)}</p>
              ) : null}
              {wallets.data ? <p className="hint">{wallets.data.earning.message}</p> : null}
              {!(me.data?.emailVerified ?? bootstrap.session.emailVerified) ? (
                <VerificationPrompt email={typeof me.data?.email === "string" ? me.data.email : null} />
              ) : null}
              <MyHomeCard signedIn={signedIn} />
              <DmSettingsCard signedIn={signedIn} enabled={bootstrap.features.privateMessagesEnabled} />
              <WorldVisibilityCard signedIn={signedIn} />
              <div className="section" data-testid="account-advertise">
                <div className="section-title">Advertising</div>
                <p className="small" style={{ margin: 0 }}>
                  Your advertiser profile, store-sign and billboard designs, billboard bookings and private statistics:{" "}
                  <Link to="/advertise">Advertiser dashboard</Link>
                </p>
              </div>
              <div className="section" data-testid="account-my-orders">
                <div className="section-title">My orders</div>
                <MyOrdersList signedIn={signedIn} limit={5} />
                <p className="small" style={{ margin: "6px 0 0" }}>
                  <Link to="/orders">All orders and receipts</Link>
                </p>
              </div>
              <TwoStepSection signedIn={signedIn} />
              <PrivacySection signedIn={signedIn} />
              <div className="row" style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className="btn"
                  aria-expanded={editing}
                  disabled={busy}
                  onClick={() => {
                    setEditing((v) => !v);
                    setAvatar(null);
                    setAvatarMessage(null);
                  }}
                >
                  {editing ? "Cancel avatar changes" : "Edit avatar"}
                </button>
                <button type="button" className="btn" disabled={busy} onClick={() => void onSignOut()}>
                  {busy ? "Working…" : "Sign out"}
                </button>
              </div>
              {avatarMessage ? (
                <div
                  className={`notice ${avatarMessage.tone === "error" ? "notice-danger" : "notice-info"}`}
                  role={avatarMessage.tone === "error" ? "alert" : "status"}
                  style={{ marginTop: 10 }}
                >
                  {avatarMessage.text}
                </div>
              ) : null}
              {editing ? (
                <form
                  className="auth-forms"
                  style={{ marginTop: 12 }}
                  data-movement-block="true"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void onSaveAvatar();
                  }}
                >
                  <AvatarBuilder value={avatarValue} onChange={setAvatar} disabled={avatarBusy} />
                  <button type="submit" className="btn btn-primary" disabled={avatarBusy}>
                    {avatarBusy ? "Saving…" : "Save avatar"}
                  </button>
                </form>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}

      {message ? (
        <div className={`notice ${message.tone === "error" ? "notice-danger" : "notice-info"}`} role={message.tone === "error" ? "alert" : "status"}>
          {message.text}
        </div>
      ) : null}

      {bootstrap && !signedIn ? (
        <div className="auth-card" style={{ marginTop: 16 }}>
          <div className="tabs" role="tablist" aria-label="Sign in or register">
            <button type="button" role="tab" className="tab" aria-selected={tab === "signin"} onClick={() => setTab("signin")}>
              Sign in
            </button>
            <button type="button" role="tab" className="tab" aria-selected={tab === "signup"} onClick={() => setTab("signup")} disabled={!registrationEnabled}>
              Create account
            </button>
          </div>
          {tab === "signin" && codeStep ? (
            <SignInCodeForm onSignedIn={onCodeSignedIn} onRestart={() => setCodeStep(false)} />
          ) : tab === "signin" && forgot ? (
            <ForgotPasswordForm initialEmail={email} onBack={() => setForgot(false)} />
          ) : tab === "signin" ? (
            <form className="auth-forms" onSubmit={(e) => void onSignIn(e)} data-movement-block="true">
              <label className="field">
                Email
                <input type="email" name="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
              </label>
              <label className="field">
                Password
                <input type="password" name="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
              </label>
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? "Signing in…" : "Sign in"}
              </button>
              <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: "flex-start" }} onClick={() => setForgot(true)} data-testid="forgot-password">
                Forgot password?
              </button>
            </form>
          ) : (
            <form className="auth-forms" onSubmit={(e) => void onSignUp(e)} data-movement-block="true">
              {!registrationEnabled ? <div className="notice notice-warning small">Registration is closed in this environment.</div> : null}
              <label className="field">
                Display name
                <input
                  type="text"
                  name="displayName"
                  autoComplete="nickname"
                  required
                  minLength={3}
                  maxLength={20}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  aria-describedby="display-name-hint"
                  aria-invalid={Boolean(nameProblem)}
                />
                <span id="display-name-hint" className={nameProblem ? "hint error-text" : "hint"}>
                  {nameProblem ?? "3–20 letters, digits or underscores, starting with a letter. Shown on your avatar and in chat."}
                </span>
              </label>
              <label className="field">
                Email
                <input type="email" name="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
              </label>
              <label className="field">
                Password
                <input
                  type="password"
                  name="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <span className="hint">At least 8 characters.</span>
              </label>
              <div className="section" style={{ marginTop: 0 }}>
                <div className="section-title">Your avatar</div>
                <AvatarBuilder value={avatarValue} onChange={setAvatar} disabled={busy} />
              </div>
              {adultsOnly ? (
                <label className="row" style={{ gap: 8, alignItems: "flex-start" }}>
                  <input
                    type="checkbox"
                    name="ageConfirmed"
                    required
                    checked={adultConfirmed}
                    onChange={(e) => setAdultConfirmed(e.target.checked)}
                    data-testid="signup-age-confirm"
                  />
                  <span>I am 18 or older.</span>
                </label>
              ) : null}
              <p className="hint" data-testid="signup-legal">
                By creating an account you agree to the <Link to="/legal/terms">Terms of Use</Link>, the{" "}
                <Link to="/legal/payments">Payments &amp; Credit Policy</Link> (payments are final and cannot be withdrawn) and the{" "}
                <Link to="/legal/privacy">Privacy Policy</Link>.
              </p>
              <button type="submit" className="btn btn-primary" disabled={busy || !registrationEnabled || (adultsOnly && !adultConfirmed)}>
                {busy ? "Creating…" : "Create account"}
              </button>
              <p className="hint">
                Accounts enable a persistent custom name and avatar. Guests cannot buy, claim rewards or own property; registration does not
                grant coins.
              </p>
            </form>
          )}
        </div>
      ) : null}

      <p style={{ marginTop: 20 }}>
        <Link to="/">Back to the town</Link> · <Link to="/directory">Directory</Link>
      </p>
    </PageShell>
  );
}

/** `/account?verified=1` is where the verification email lands (Better Auth adds `error` when the link failed). */
export function VerifiedBanner({ verified, error }: { verified: boolean; error: string | null }) {
  if (error && (verified || /token/i.test(error))) {
    return (
      <div className="notice notice-danger" role="alert" style={{ marginBottom: 12 }} data-testid="verify-error">
        That verification link is invalid or has expired. Sign in and use "Resend verification email" to get a new one.
      </div>
    );
  }
  if (!verified) return null;
  return (
    <div className="notice notice-success" role="status" style={{ marginBottom: 12 }} data-testid="verified-banner">
      Email verified. You can buy plots and items now.
    </div>
  );
}

const RESEND_COOLDOWN_MS = 60_000;

/** Signed-in residents with an unverified address can ask for a fresh link (rate-limited here and on the server). */
function VerificationPrompt({ email }: { email: string | null }) {
  const [busy, setBusy] = useState(false);
  const [coolingDown, setCoolingDown] = useState(false);
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const resend = async () => {
    if (!email || busy || coolingDown) return;
    setBusy(true);
    setMessage(null);
    const res = await resendVerificationEmail(email);
    setBusy(false);
    if (res.ok) {
      setCoolingDown(true);
      setTimeout(() => setCoolingDown(false), RESEND_COOLDOWN_MS);
      setMessage({ tone: "success", text: `Verification email sent to ${email}. Check your inbox (and spam folder) for the link.` });
    } else {
      setMessage({ tone: "error", text: res.message ?? "The verification email could not be sent. Try again in a minute." });
    }
  };
  return (
    <div className="notice notice-warning small" style={{ marginTop: 10 }} data-testid="verify-prompt">
      Verify your email address to buy plots, upgrades and items.{" "}
      <button type="button" className="btn btn-sm" disabled={!email || busy || coolingDown} onClick={() => void resend()} data-testid="resend-verification">
        {busy ? "Sending…" : coolingDown ? "Sent — check your inbox" : "Resend verification email"}
      </button>
      {message ? (
        <div role={message.tone === "error" ? "alert" : "status"} className={message.tone === "error" ? "error-text" : undefined} style={{ marginTop: 6 }}>
          {message.text}
        </div>
      ) : null}
    </div>
  );
}

/** Request a reset link. The confirmation is the same whether or not the address has an account. */
function ForgotPasswordForm({ initialEmail, onBack }: { initialEmail: string; onBack(): void }) {
  const [email, setEmail] = useState(initialEmail);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    const res = await requestPasswordReset(trimmed);
    setBusy(false);
    if (res.ok) setSent(true);
    else setError(res.message ?? "The reset email could not be sent. Try again in a minute.");
  };
  return (
    <form className="auth-forms" onSubmit={(e) => void onSubmit(e)} data-movement-block="true" data-testid="forgot-form">
      <p className="hint" style={{ margin: 0 }}>
        Enter the email address of your account and we'll send a link to choose a new password.
      </p>
      <label className="field">
        Email
        <input type="email" name="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </label>
      {sent ? (
        <div className="notice notice-info small" role="status" data-testid="forgot-sent">
          If an account exists for {email.trim()}, a reset link is on its way. The link works for a limited time.
        </div>
      ) : null}
      {error ? (
        <div className="notice notice-danger small" role="alert">
          {error}
        </div>
      ) : null}
      <div className="row">
        <button type="submit" className="btn btn-primary" disabled={busy || !email.trim()} data-testid="forgot-submit">
          {busy ? "Sending…" : sent ? "Send again" : "Send reset link"}
        </button>
        <button type="button" className="btn" onClick={onBack}>
          Back to sign in
        </button>
      </div>
    </form>
  );
}

/** The resident's home with the ways to customize it (3D town, or the 2D directory). */
function MyHomeCard({ signedIn }: { signedIn: boolean }) {
  const q = useMyHomeQuery(signedIn);
  const home = q.data?.home ?? null;
  if (!signedIn || !q.data) return null;
  return (
    <div className="section" data-testid="account-my-home">
      <div className="section-title">My home</div>
      {home ? (
        <>
          <p className="small" style={{ margin: "0 0 6px" }}>
            <strong>{home.address}</strong> · {stageLabel(home.stage)} · {home.access === "closed" ? "closed to visitors" : "open to visitors"}. Homes can never be
            taken over by other players.
          </p>
          <div className="row">
            <Link className="btn btn-sm btn-primary" to="/?myhome=1" data-testid="account-customize-home">
              Customize my home
            </Link>
            <Link className="btn btn-sm" to="/directory#my-home">
              Customize in the text version
            </Link>
            <Link className="btn btn-sm" to="/directory#home-builders">
              Upgrade at Home Builders
            </Link>
          </div>
        </>
      ) : (
        <p className="small" style={{ margin: 0 }}>
          You don't own a home yet. <Link to="/directory#real-estate">Buy a plot at Townsy Real Estate</Link>: it starts as a tent.
        </p>
      )}
    </div>
  );
}
